// Media Portal API Client - Uses EMS auth token for unified access
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/mp`;

const api = axios.create({
  baseURL: API,
  headers: { 'Content-Type': 'application/json' },
});

// Use EMS token from localStorage for unified auth
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('ems_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const getToken = () => localStorage.getItem('ems_token');

// Auth API (for MP-specific auth like /me)
export const authApi = {
  getMe: async () => {
    const res = await api.get('/auth/me');
    return res.data;
  },
};

// Users API
export const usersApi = {
  getAll: async () => (await api.get('/users')).data,
  getById: async (id) => (await api.get(`/users/${id}`)).data,
  create: async (data) => (await api.post('/users', data)).data,
  update: async (id, data) => (await api.put(`/users/${id}`, data)).data,
  updatePassword: async (id, password) => (await api.put(`/users/${id}/password`, { password })).data,
  delete: async (id) => (await api.delete(`/users/${id}`)).data,
};

// Exercises API
export const exercisesApi = {
  getAll: async () => (await api.get('/exercises')).data,
  getActive: async () => (await api.get('/exercises/active')).data,
  getById: async (id) => (await api.get(`/exercises/${id}`)).data,
  create: async (data) => (await api.post('/exercises', data)).data,
  update: async (id, data) => (await api.put(`/exercises/${id}`, data)).data,
  delete: async (id) => (await api.delete(`/exercises/${id}`)).data,
  publish: async (id) => (await api.post(`/exercises/${id}/publish`)).data,
  unpublish: async (id) => (await api.post(`/exercises/${id}/unpublish`)).data,
  enable: async (id) => (await api.post(`/exercises/${id}/enable`)).data,
  disable: async (id) => (await api.post(`/exercises/${id}/disable`)).data,
  duplicate: async (id) => (await api.post(`/exercises/${id}/duplicate`)).data,
  getStats: async (id) => (await api.get(`/exercises/${id}/stats`)).data,
  getEmsExercises: async () => (await api.get('/exercises/ems-exercises/list')).data,
  setGuestPassword: async (id, password) => (await api.post(`/exercises/${id}/guest-password`, { password })).data,
};

// Posts API
export const postsApi = {
  getAll: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.exercise_id) params.append('exercise_id', filters.exercise_id);
    if (filters.platform) params.append('platform', filters.platform);
    if (filters.status) params.append('status', filters.status);
    if (filters.post_type) params.append('post_type', filters.post_type);
    return (await api.get(`/posts?${params.toString()}`)).data;
  },
  getById: async (id) => (await api.get(`/posts/${id}`)).data,
  create: async (data) => (await api.post('/posts', data)).data,
  update: async (id, data) => (await api.put(`/posts/${id}`, data)).data,
  delete: async (id) => (await api.delete(`/posts/${id}`)).data,
  publish: async (id) => (await api.post(`/posts/${id}/publish`)).data,
  unpublish: async (id) => (await api.post(`/posts/${id}/unpublish`)).data,
  reorder: async (order) => (await api.patch(`/posts/reorder`, { order })).data,
  downloadTemplate: async (format = 'xlsx') => {
    const res = await api.get(`/posts/template?format=${format}`, { responseType: 'blob' });
    const ext = format === 'csv' ? 'csv' : 'xlsx';
    const url = URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = `mp_bulk_import_template.${ext}`;
    a.click();
    URL.revokeObjectURL(url);
  },
  bulkImport: async (file, exerciseId) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('exercise_id', exerciseId);
    return (await api.post('/posts/bulk-import', formData, { headers: { 'Content-Type': 'multipart/form-data' } })).data;
  },
};

// Media API
export const mediaApi = {
  getAll: async (fileType = null) => {
    const params = fileType ? `?file_type=${fileType}` : '';
    return (await api.get(`/media${params}`)).data;
  },
  getStats: async () => (await api.get('/media/stats')).data,
  upload: async (file, caption = '') => {
    const formData = new FormData();
    formData.append('file', file);
    if (caption) formData.append('caption', caption);
    return (await api.post('/media/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } })).data;
  },
  delete: async (id) => (await api.delete(`/media/${id}`)).data,
};

// Platforms API
export const platformsApi = {
  getAll: async () => (await api.get('/platforms')).data,
  create: async (data) => (await api.post('/platforms', data)).data,
  update: async (id, data) => (await api.put(`/platforms/${id}`, data)).data,
  delete: async (id) => (await api.delete(`/platforms/${id}`)).data,
};

// Audit API
export const auditApi = {
  getAll: async (limit = 100, filters = {}) => {
    const params = new URLSearchParams({ limit: limit.toString() });
    if (filters.entity_type) params.append('entity_type', filters.entity_type);
    if (filters.action) params.append('action', filters.action);
    return (await api.get(`/audit?${params.toString()}`)).data;
  },
};

// Settings API
export const settingsApi = {
  getAll: async () => (await api.get('/settings')).data,
  update: async (data) => (await api.put('/settings', data)).data,
};

// AI Generation API
export const aiApi = {
  generatePost: async (data) => (await api.post('/ai/generate-post', data)).data,
  generateImage: async (data) => (await api.post('/ai/generate-image', data)).data,
  generateAuthor: async (platform = 'twitter', region = 'global') => (await api.post(`/ai/generate-author?platform=${platform}&region=${region}`)).data,
};

// V14.13 — Send the X-Guest-Pass header (HMAC, 12h) on every guest call
// when one exists in sessionStorage. Backend uses it to satisfy the
// optional per-exercise password gate.
const guestHeaders = (guestToken) => {
  try {
    const pass = sessionStorage.getItem(`mp_guest_pass_${guestToken}`);
    return pass ? { 'X-Guest-Pass': pass } : {};
  } catch { return {}; }
};

// Guest Access API
export const guestApi = {
  getExercise: async (guestToken) => (await axios.get(`${API}/guest/exercise/${guestToken}`, { headers: guestHeaders(guestToken) })).data,
  register: async (name, guestToken, turnstileToken = null) => {
    const params = new URLSearchParams();
    params.append('name', name);
    params.append('guest_token', guestToken);
    if (turnstileToken) params.append('turnstile_token', turnstileToken);
    return (await axios.post(`${API}/guest/register?${params.toString()}`, null, { headers: guestHeaders(guestToken) })).data;
  },
  getPosts: async (guestToken, platform = null) => {
    const params = platform ? `?platform=${platform}` : '';
    return (await axios.get(`${API}/guest/posts/${guestToken}${params}`, { headers: guestHeaders(guestToken) })).data;
  },
  createPost: async (data, guestId = null, guestToken = null) => {
    const params = new URLSearchParams();
    params.append('exercise_id', data.exercise_id);
    params.append('platform', data.platform);
    params.append('content', data.content);
    if (data.title) params.append('title', data.title);
    if (data.post_type) params.append('post_type', data.post_type);
    if (guestId) params.append('guest_id', guestId);
    if (guestToken) params.append('guest_token', guestToken);
    const token = getToken();
    return (await axios.post(`${API}/guest/posts?${params.toString()}`, null, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })).data;
  },
};

// Interactions API (comments, reactions)
export const interactionsApi = {
  getComments: async (postId) => (await axios.get(`${API}/guest/comments/${postId}`)).data,
  createComment: async (data, guestId = null, guestToken = null) => {
    const params = new URLSearchParams();
    if (guestId) params.append('guest_id', guestId);
    if (guestToken) params.append('guest_token', guestToken);
    const token = getToken();
    return (await axios.post(`${API}/guest/comments?${params.toString()}`, data, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })).data;
  },
  getReactions: async (postId) => (await axios.get(`${API}/guest/reactions/${postId}`)).data,
  createReaction: async (data, guestId = null, guestToken = null) => {
    const params = new URLSearchParams();
    if (guestId) params.append('guest_id', guestId);
    if (guestToken) params.append('guest_token', guestToken);
    const token = getToken();
    return (await axios.post(`${API}/guest/reactions?${params.toString()}`, data, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })).data;
  },
};

// Dashboard API
export const dashboardApi = {
  getStats: async () => {
    const token = getToken();
    return (await axios.get(`${BACKEND_URL}/api/mp/dashboard`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })).data;
  },
};

export default api;
